import type { KeyboardEvent } from "react"
import { memo } from "react"

import { cn } from "@productlane/lib/src/cn"
import { formatRelativeTime } from "@productlane/lib/src/date"

import type { NotificationGroup } from "./group-notifications"
import type { NotificationListRow } from "./use-notifications"
import { SelectionCheckbox } from "@/components/selection-checkbox"
import {
	ActorAvatar,
	AgentAvatar,
	CompanyAvatar,
	CustomerAvatar,
	TypeBadge,
} from "./actor-avatar"
import { NOTIFICATIONS_COPY as copy } from "./copy"
import { notificationTypeIcon, threadStateIcon } from "./notification-icons"
import {
	notificationAvatar,
	notificationText,
} from "./notification-presentation"
import { NotificationTitle } from "./notification-title"

type Group = NotificationGroup<NotificationListRow>

export interface RowClickModifiers {
	shift: boolean
	toggle: boolean
}

interface Props {
	group: Group
	isCursor: boolean
	isChecked: boolean
	/** Once anything is checked every row shows its checkbox, not just the hovered one. */
	isSelecting: boolean
	onClick: (group: Group, modifiers: RowClickModifiers) => void
	onToggleChecked: (group: Group) => void
}

export const NotificationRow = memo(
	({
		group,
		isCursor,
		isChecked,
		isSelecting,
		onClick,
		onToggleChecked,
	}: Props) => {
		const latest = group.rows[0]
		if (!latest) return null

		const isUnread = group.unreadIds.length > 0
		const { title, body } = notificationText(latest)
		const TypeIcon = notificationTypeIcon(latest.type)
		const avatar = notificationAvatar(latest)
		const stateIcon = threadStateIcon(latest.thread?.state)
		const showCheckbox = isSelecting || isChecked
		const dim = !isUnread && !isCursor && "opacity-70"

		return (
			<div
				role="button"
				tabIndex={0}
				aria-current={isCursor ? "true" : undefined}
				data-notification-group={group.key}
				onClick={(event) =>
					onClick(group, {
						shift: event.shiftKey,
						toggle: event.metaKey || event.ctrlKey,
					})
				}
				onKeyDown={(event: KeyboardEvent) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault()
						onClick(group, { shift: false, toggle: false })
					}
				}}
				className={cn(
					"group/row flex w-full cursor-default items-start gap-2.5 border-b border-gray-100 px-3 py-2.5 outline-none focus-visible:bg-gray-100 dark:border-gray-800/60 dark:focus-visible:bg-gray-800/50",
					isCursor
						? "bg-gray-100 dark:bg-gray-800/50"
						: isChecked
							? "bg-gray-50 dark:bg-gray-800/30"
							: "hover:bg-gray-50 dark:hover:bg-gray-800/30",
				)}
			>
				{/* Avatar and checkbox share one slot; both stay mounted so hover never reflows. */}
				<div className="relative flex h-7 w-7 flex-none items-center justify-center">
					<div
						className={cn(
							"relative flex h-7 w-7 items-center justify-center",
							showCheckbox
								? "opacity-0"
								: "group-focus-within/row:opacity-0 group-hover/row:opacity-0",
						)}
					>
						<div className="relative flex h-7 w-7 items-center justify-center">
							{avatar.kind === "customer" ? (
								<CustomerAvatar contact={avatar.contact} />
							) : avatar.kind === "company" ? (
								<CompanyAvatar company={avatar.company} />
							) : avatar.kind === "actor" && latest.actor ? (
								<ActorAvatar user={latest.actor} size="row" />
							) : avatar.kind === "agent" ? (
								<AgentAvatar />
							) : (
								<span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 text-secondary dark:bg-gray-800">
									<TypeIcon className="h-4 w-4 flex-none transform-gpu" />
								</span>
							)}
							{avatar.kind !== "icon" && <TypeBadge Icon={TypeIcon} />}
						</div>
					</div>
					<div
						className={cn(
							"absolute inset-0 flex items-center justify-center",
							showCheckbox
								? "opacity-100"
								: "opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100",
						)}
					>
						<SelectionCheckbox
							label={copy.selection.select}
							checked={isChecked}
							onCheckedChange={() => onToggleChecked(group)}
						/>
					</div>
				</div>

				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<div className="flex items-baseline justify-between gap-2">
						<NotificationTitle
							title={title}
							isUnread={isUnread}
							className={cn(dim)}
						/>
						<span
							className={cn(
								"flex flex-none items-center gap-1 text-[11px] tabular-nums text-tertiary",
								dim,
							)}
						>
							{stateIcon && (
								<stateIcon.Icon
									aria-hidden
									className={cn(
										"h-3.5 w-3.5 flex-none transform-gpu",
										stateIcon.className,
									)}
								/>
							)}
							{formatRelativeTime(new Date(latest.createdAt))}
						</span>
					</div>
					<p
						className={cn(
							"truncate text-xs",
							isUnread ? "text-secondary" : "text-tertiary",
							dim,
						)}
					>
						{body}
					</p>
				</div>
			</div>
		)
	},
)

NotificationRow.displayName = "NotificationRow"
